import React from 'react';
import { Building, Mail, Phone, Globe, Calendar, DollarSign, Circle, Flag, UserCheck, PhoneOff, Trophy, Megaphone, Stethoscope } from 'lucide-react';
import {
  formatName,
  formatEmail,
  formatPhone,
  formatCompany,
  formatDate,
  formatCurrency,
  getSourceText,
  getPriorityColor,
} from '../utils/formatters';
import BrowserCallButton from '../components/BrowserCallButton';
import AdAttributionBadge from '../components/AdAttributionBadge';
import OpenDentalSyncBadge from '../components/OpenDentalSyncBadge';
import { getAdAttribution } from '../utils/adAttribution';

// Column registry for the leads list (LeadsList and every view in
// leadViews.js). One entry per column: the picker, the header and the cells
// all come from here, so a column is declared once.
//
//   key        visibility key (saved per view in localStorage)
//   label      picker and header text
//   sortKey    server sort field
//   icon       header icon (optional)
//   width      fixed width class shared by header and cell (optional)
//   gate(ctx)  false hides the column even when switched on
//   render(lead, ctx)  cell content
//
// ctx: { currentUser, organization, stages, userById, usersLoaded, claim,
//        onStageChange, onPriorityChange, onClaimed, linkBase }

export const ownerInitials = (name = '', email = '') => {
  const src = (name || email || '').trim();
  if (!src) return '?';
  const p = src.split(/\s+/).filter(Boolean);
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
};

export const ownerColor = (id = '') => {
  const palette = ['bg-blue-100 text-blue-800', 'bg-emerald-100 text-emerald-800', 'bg-amber-100 text-amber-800', 'bg-purple-100 text-purple-800', 'bg-rose-100 text-rose-800', 'bg-cyan-100 text-cyan-800'];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
};

// tel: link that claims the lead first (first come, first served), plus the
// browser call button behind the same guard.
const ClaimingPhone = ({ lead, phone, claim }) => (
  <span className="inline-flex items-center gap-1.5">
    <a
      href={`tel:${phone}`}
      className="text-primary-600 hover:text-primary-700 font-medium"
      onClick={(e) => {
        // FCFS — clicking the phone claims the lead.
        e.preventDefault();
        claim.requestClaimFor(lead, () => { window.location.href = `tel:${phone}`; });
      }}
    >
      {formatPhone(phone)}
    </a>
    <BrowserCallButton lead={lead} phone={phone} guard={(go) => claim.requestClaimFor(lead, go)} />
  </span>
);

// OpenDental appointment times are practice-local "YYYY-MM-DD HH:mm:ss"
// strings. Read the parts as they are (no Date, so no time zone shift):
// "Oct 20, 2026 10:00".
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function appointmentLabel(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(value || ''));
  if (!m || !MONTHS[+m[2] - 1]) return String(value || '');
  return `${MONTHS[+m[2] - 1]} ${m[3]}, ${m[1]} ${m[4]}:${m[5]}`;
}

export const LEAD_COLUMNS = [
  {
    key: 'name',
    label: 'Name',
    sortKey: 'firstName',
    render: (lead) => (
      <div className="flex items-center gap-2">
        {lead.status === 'do_not_call' && (
          <PhoneOff size={14} className="text-gray-500 flex-shrink-0" />
        )}
        {lead.tags?.includes('owner-contact') && lead.status !== 'do_not_call' && (
          <UserCheck size={14} className="text-green-600 flex-shrink-0" title="Owner direct contact" />
        )}
        <div>
          <div className={`text-sm font-medium truncate ${lead.status === 'do_not_call' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
            {formatName(lead.firstName, lead.lastName)}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {lead.position}
          </div>
        </div>
      </div>
    ),
  },
  {
    key: 'company',
    label: 'Company',
    sortKey: 'company',
    icon: Building,
    gate: (ctx) => !!ctx.currentUser?.permissions?.view_company_info,
    render: (lead) => (
      <div className="text-sm text-gray-900 truncate">
        {formatCompany(lead.company)}
      </div>
    ),
  },
  {
    key: 'email',
    label: 'Email',
    sortKey: 'email',
    icon: Mail,
    render: (lead) => (
      <div className="text-sm text-gray-900 truncate">
        {formatEmail(lead.email)}
      </div>
    ),
  },
  {
    key: 'phone',
    label: 'Phone',
    sortKey: 'phone',
    icon: Phone,
    render: (lead, ctx) => (
      <div className="text-sm">
        {lead.phone ? (
          lead.status === 'do_not_call' ? (
            <span className="text-gray-400 line-through">
              {formatPhone(lead.phone)}
            </span>
          ) : (
            <ClaimingPhone lead={lead} phone={lead.phone} claim={ctx.claim} />
          )
        ) : '-'}
      </div>
    ),
  },
  {
    key: 'companyPhone',
    label: 'Company Phone',
    sortKey: 'companyPhone',
    icon: Phone,
    render: (lead, ctx) => (
      <div className="text-sm">
        {lead.companyPhone ? (
          <ClaimingPhone lead={lead} phone={lead.companyPhone} claim={ctx.claim} />
        ) : '-'}
      </div>
    ),
  },
  {
    key: 'website',
    label: 'Website',
    sortKey: 'website',
    icon: Globe,
    render: (lead) => (
      <div className="text-sm">
        {lead.website ? (
          <a
            href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:text-primary-700 hover:underline truncate max-w-[200px] inline-block"
            title={lead.website}
          >
            {lead.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          </a>
        ) : '-'}
      </div>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    sortKey: 'stageId',
    icon: Circle,
    width: 'w-32',
    render: (lead, ctx) => {
      const currentStage = ctx.stages.find(
        (s) => String(s._id) === String(lead.stageId)
      );
      const bg = currentStage?.color ? `${currentStage.color}22` : '#F3F4F6';
      const fg = currentStage?.color || '#374151';
      return (
        <select
          value={lead.stageId || ''}
          onChange={(e) => ctx.onStageChange(lead, e.target.value)}
          style={{ backgroundColor: bg, color: fg, minWidth: '110px' }}
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border-0 cursor-pointer focus:ring-2 focus:ring-offset-1 focus:ring-primary-500 transition-colors"
        >
          {ctx.stages.map((s) => (
            <option key={s._id} value={s._id}>{s.name}</option>
          ))}
        </select>
      );
    },
  },
  {
    key: 'priority',
    label: 'Priority',
    sortKey: 'priority',
    icon: Flag,
    width: 'w-24',
    render: (lead, ctx) => (
      <select
        value={lead.priority}
        onChange={(e) => ctx.onPriorityChange(lead._id, e.target.value)}
        style={{ width: '90px' }}
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border-0 cursor-pointer focus:ring-2 focus:ring-offset-1 focus:ring-primary-500 transition-colors ${getPriorityColor(lead.priority)}`}
      >
        <option value="low">Low</option>
        <option value="medium">Medium</option>
        <option value="high">High</option>
      </select>
    ),
  },
  {
    key: 'source',
    label: 'Source',
    sortKey: 'source',
    render: (lead) => (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        {getSourceText(lead.source)}
      </span>
    ),
  },
  {
    key: 'date',
    label: 'Date',
    sortKey: 'createdAt',
    icon: Calendar,
    render: (lead) => (
      <div className="text-sm text-gray-900">
        {formatDate(lead.createdAt)}
      </div>
    ),
  },
  {
    key: 'value',
    label: 'Value',
    sortKey: 'estimatedValue',
    icon: DollarSign,
    render: (lead) => (
      <div className="text-sm text-gray-900">
        {formatCurrency(lead.estimatedValue, lead.currency)}
      </div>
    ),
  },
  {
    key: 'owner',
    label: 'Owner',
    sortKey: 'assignedTo',
    icon: UserCheck,
    width: 'w-32',
    render: (lead, ctx) => {
      const u = ctx.userById.get(String(lead.assignedTo));
      if (!u && lead.assignedTo && ctx.usersLoaded) {
        // Owned by someone no longer on the team: not claimable from here
        // (the server would refuse), so say so instead of offering Take.
        return <span className="text-xs text-gray-400 italic">Inactive user</span>;
      }
      if (!u) {
        // FCFS quick-claim. The page keeps leads in local state (not
        // react-query), so the hook's invalidateQueries doesn't repaint the
        // list on its own. onClaimed runs AFTER the claim resolves and
        // patches the row in place: assignedTo flips to the current user and
        // the owner badge takes over without a refresh.
        return (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              ctx.claim.requestClaimFor(lead, () => ctx.onClaimed(lead));
            }}
            className="inline-flex items-center gap-1 rounded-full border border-primary-300 bg-primary-50 px-2 py-1 text-[11px] font-medium text-primary-700 hover:bg-primary-100"
            title="Claim this lead (FCFS)"
          >
            🔓 Take
          </button>
        );
      }
      return (
        <div className="flex items-center gap-2" title={u.email}>
          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${ownerColor(String(u._id))}`}>
            {ownerInitials(u.name, u.email)}
          </span>
          <span className="text-sm text-gray-700 truncate max-w-[8rem]">{u.name || u.email}</span>
        </div>
      );
    },
  },
];

// Columns that matter once a lead is a customer (leadViews.customers).
LEAD_COLUMNS.push(
  {
    key: 'wonAt',
    label: 'Won On',
    sortKey: 'wonAt',
    icon: Trophy,
    render: (lead) => (
      <div className="text-sm text-gray-900" title={lead.wonAtApprox ? 'Approximate: won before won dates were recorded' : undefined}>
        {formatDate(lead.wonAt || lead.stageUpdatedAt)}
        {lead.wonAtApprox && <span className="text-gray-400"> <span aria-hidden="true">≈</span><span className="sr-only">(approximate)</span></span>}
      </div>
    ),
  },
  {
    key: 'attribution',
    label: 'Ad / Source',
    sortKey: 'source',
    icon: Megaphone,
    render: (lead, ctx) => (getAdAttribution(lead)
      ? <AdAttributionBadge lead={lead} compact maxCrumbs={1} basePath={ctx.linkBase} className="max-w-[16rem]" />
      : (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {getSourceText(lead.source)}
        </span>
      )),
  },
  {
    key: 'opendental',
    label: 'OpenDental',
    sortKey: 'opendental.syncStatus',
    icon: Stethoscope,
    gate: (ctx) => !!ctx.organization?.opendental?.enabled,
    render: (lead) => {
      const od = lead.opendental || {};
      const status = od.syncStatus && od.syncStatus !== 'none' ? od.syncStatus : null;
      return (
        <div className="flex flex-col gap-0.5">
          {status
            ? <OpenDentalSyncBadge status={status} syncedLabel="Booked" className="w-fit" />
            : <span className="inline-flex w-fit items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Not booked</span>}
          {od.appointmentAt && <span className="text-xs text-gray-500">{appointmentLabel(od.appointmentAt)}</span>}
        </div>
      );
    },
  },
);

export const LEAD_COLUMNS_BY_KEY = Object.fromEntries(LEAD_COLUMNS.map((c) => [c.key, c]));

/** Column entries for the org's lead custom fields (keys cf_<key>). */
export function customFieldColumns(customFields) {
  return (customFields || []).map((cf) => ({
    key: `cf_${cf.key}`,
    label: cf.label,
    sortKey: `customFields.${cf.key}`,
    custom: true,
    render: (lead) => {
      const raw = lead.customFields?.[cf.key];
      let display = '';
      if (raw !== undefined && raw !== null && raw !== '') {
        if (cf.type === 'checkbox') display = raw ? 'Yes' : 'No';
        else if (cf.type === 'date') display = raw ? new Date(raw).toLocaleDateString() : '';
        else display = String(raw);
      }
      return display || <span className="text-gray-300">—</span>;
    },
  }));
}

const TH_BASE = 'px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
const TD_BASE = 'px-3 py-3 whitespace-nowrap';

// hideBelow ('md' | 'lg', per view) hides a column on narrower screens.
const HIDE = { md: 'hidden md:table-cell', lg: 'hidden lg:table-cell' };
const withHide = (cls, hideBelow) => (HIDE[hideBelow] ? `${HIDE[hideBelow]} ${cls}` : cls);

export const headerClassFor = (col, hideBelow) => withHide(col.width ? `${col.width} ${TH_BASE}` : TH_BASE, hideBelow);
export const cellClassFor = (col, hideBelow) => {
  if (col.custom) return withHide(`${TD_BASE} text-sm text-gray-700`, hideBelow);
  return withHide(col.width ? `${col.width} ${TD_BASE}` : TD_BASE, hideBelow);
};
